'use client';

import { useParams } from 'next/navigation';
import type { Document } from '@contentful/rich-text-types';

const HEADING_2 = 'heading-2';
const HEADING_3 = 'heading-3';

import Text from '@/components/ui/Text';
import useActiveHeading from '@/hooks/useActiveHeading';
import { BLOG_I18N } from '@/lib/i18n/blogI18n';
import { resolveAppLocale } from '@/lib/i18n/routing';

type Heading = { id: string; text: string; level: 2 | 3 };

function extractHeadings(doc: Document): Heading[] {
  return doc.content.flatMap((node) => {
    if (node.nodeType !== HEADING_2 && node.nodeType !== HEADING_3) return [];

    const text = node.content
      .map((c) => ('value' in c ? c.value : ''))
      .join('');
    if (!text) return [];

    const id = text
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]/g, '');
    const level: 2 | 3 = node.nodeType === HEADING_2 ? 2 : 3;
    return [{ id, text, level }];
  });
}

const TableOfContents = ({ document: doc }: TableOfContentsProps) => {
  const { locale } = useParams<{ locale: string }>();
  const t = BLOG_I18N[resolveAppLocale(locale)];
  const headings = extractHeadings(doc);
  const activeId = useActiveHeading(headings.map((h) => h.id));

  if (headings.length === 0) return null;

  return (
    <aside className="hidden desktop:block w-56 shrink-0">
      <div className="sticky top-28">
        <Text as="p" variant="sectionEyebrow" className="mb-4">
          {t.tableOfContents}
        </Text>
        <nav>
          <ul className="space-y-2">
            {headings.map(({ id, text }) => (
              <li key={id} className={`flex items-start gap-2`}>
                <span
                  className={`mt-[6px] w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${activeId === id ? 'bg-primary' : 'bg-text-secondary'}`}
                />
                <a
                  href={`#${id}`}
                  className={`text-sm leading-snug font-medium transition-colors hover:text-primary ${
                    activeId === id ? 'text-primary' : 'text-text-secondary'
                  }`}
                >
                  {text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default TableOfContents;

type TableOfContentsProps = {
  document: Document;
};
